import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { 
  Mail, 
  MessageSquare, 
  Download, 
  Calendar, 
  User, 
  Phone,
  Building,
  Clock,
  CheckCircle,
  Eye,
  Filter,
  Search
} from 'lucide-react';

const CommunicationsPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('newsletter');
  const [newsletters, setNewsletters] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    if (activeTab === 'newsletter') {
      fetchNewsletters();
    } else {
      fetchContacts();
    }
  }, [activeTab, token]);

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      // Note: This endpoint doesn't exist yet, need to create it
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/newsletter-subscribers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewsletters(data);
      }
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/contact-submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportNewsletters = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/export-emails`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Export Successful",
          description: "Newsletter subscribers exported to CSV",
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export newsletter subscribers",
        variant: "destructive"
      });
    }
  };

  const sendNewsletter = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-newsletter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days_back: 7 }) // Send weekly newsletter
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Newsletter sent successfully!",
          description: `Sent to ${result.emails_sent} subscribers. ${result.trucks_included} trucks included.`,
          variant: "default"
        });
      } else {
        throw new Error('Failed to send newsletter');
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
      toast({
        title: "Error",
        description: "Failed to send newsletter. Please try again.",
        variant: "destructive"
      });
    }
  };

  const filteredNewsletters = newsletters.filter(item => 
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContacts = contacts.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-gray-900 font-inter uppercase">
            Communications
          </h1>
          <p className="text-gray-600 mt-2 font-inter">
            Manage newsletter subscribers and customer inquiries
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('newsletter')}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'newsletter'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Mail className="h-5 w-5 inline mr-2" />
                Newsletter Subscribers ({newsletters.length})
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MessageSquare className="h-5 w-5 inline mr-2" />
                Contact Submissions ({contacts.length})
              </button>
            </nav>
          </div>

          {/* Search and Actions */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={activeTab === 'newsletter' ? "Search subscribers..." : "Search submissions..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                />
              </div>
              {activeTab === 'newsletter' && (
                <div className="flex gap-3">
                  <button
                    onClick={sendNewsletter}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Mail className="h-5 w-5" />
                    Send Weekly Newsletter
                  </button>
                  <button
                    onClick={exportNewsletters}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="h-5 w-5" />
                    Export CSV
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            ) : (
              <>
                {activeTab === 'newsletter' ? (
                  <div className="space-y-4">
                    {filteredNewsletters.length === 0 ? (
                      <div className="text-center py-12">
                        <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No newsletter subscribers found</p>
                      </div>
                    ) : (
                      filteredNewsletters.map((subscriber, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <User className="h-5 w-5 text-gray-400" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {subscriber.full_name || 'Anonymous'}
                                  </p>
                                  <p className="text-orange-600 font-medium">{subscriber.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  <span>{formatDate(subscriber.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                                    {subscriber.source}
                                  </span>
                                </div>
                                {subscriber.marketing_consent && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span>Consented</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredContacts.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No contact submissions found</p>
                      </div>
                    ) : (
                      filteredContacts.map((submission, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <User className="h-5 w-5 text-gray-400" />
                                <div>
                                  <p className="font-medium text-gray-900">{submission.name}</p>
                                  <p className="text-orange-600 font-medium">{submission.email}</p>
                                </div>
                              </div>
                              <h3 className="font-bold text-lg text-gray-900 mb-2">
                                {submission.subject}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  <span>{formatDate(submission.created_at)}</span>
                                </div>
                                {submission.phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-4 w-4" />
                                    <span>{submission.phone}</span>
                                  </div>
                                )}
                                {submission.company && (
                                  <div className="flex items-center gap-1">
                                    <Building className="h-4 w-4" />
                                    <span>{submission.company}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded border-l-4 border-orange-500">
                            <p className="text-gray-700 whitespace-pre-wrap">{submission.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsPage;